import { z } from "zod";
import { FUEL_INGEST_REJECTION_CODES, FUEL_INGEST_REJECTION_LABELS } from "../fuel/ingest.js";
/**
 * İstasyon ajanı — ortak sabitler ve Türkçe etiketler (Prompt 14).
 * Bağlayıcı sözleşme: `docs/domain/agent-protocol.md`. Kod / alan adları İngilizce, etiketler Türkçe.
 */
// ── Ajan durumu ──────────────────────────────────────────────────────────────
export const AGENT_STATUSES = ["ACTIVE", "REVOKED"];
export const agentStatusSchema = z.enum(AGENT_STATUSES);
export const AGENT_STATUS_LABELS = {
    ACTIVE: "Etkin",
    REVOKED: "İptal edilmiş",
};
export const AGENT_OS_LIST = ["windows", "linux", "darwin"];
export const agentOsSchema = z.enum(AGENT_OS_LIST);
export const AGENT_OS_LABELS = {
    windows: "Windows",
    linux: "Linux",
    darwin: "macOS",
};
export const AGENT_ARCH_LIST = ["amd64", "arm64"];
export const agentArchSchema = z.enum(AGENT_ARCH_LIST);
export const AGENT_UPDATE_CHANNELS = ["stable", "beta"];
export const agentUpdateChannelSchema = z.enum(AGENT_UPDATE_CHANNELS);
export const AGENT_UPDATE_CHANNEL_LABELS = {
    stable: "Kararlı",
    beta: "Beta",
};
// ── Komutlar ─────────────────────────────────────────────────────────────────
export const AGENT_COMMAND_TYPES = [
    "UPDATE_PRICES",
    "UPDATE_CONFIG",
    "RESTART_ADAPTER",
    "UPLOAD_LOGS",
    "REFRESH_MASTER_DATA",
    // Prompt 18: uzaktan yıkama başlatma (müşteri uygulaması / web).
    "WASH_START",
];
export const agentCommandTypeSchema = z.enum(AGENT_COMMAND_TYPES);
export const AGENT_COMMAND_TYPE_LABELS = {
    UPDATE_PRICES: "Fiyatları güncelle",
    UPDATE_CONFIG: "Yapılandırmayı güncelle",
    RESTART_ADAPTER: "Adaptörü yeniden başlat",
    UPLOAD_LOGS: "Günlükleri gönder",
    REFRESH_MASTER_DATA: "Ana veriyi yenile",
    WASH_START: "Yıkama programını başlat",
};
export const AGENT_COMMAND_STATUSES = [
    "PENDING",
    "DELIVERED",
    "ACKED",
    "SUCCEEDED",
    "FAILED",
    "EXPIRED",
    "CANCELLED",
];
export const agentCommandStatusSchema = z.enum(AGENT_COMMAND_STATUSES);
export const AGENT_COMMAND_STATUS_LABELS = {
    PENDING: "Bekliyor",
    DELIVERED: "Ajana iletildi",
    ACKED: "Ajan aldı",
    SUCCEEDED: "Başarılı",
    FAILED: "Başarısız",
    EXPIRED: "Süresi doldu",
    CANCELLED: "İptal edildi",
};
/** Sonuçlanmış (bir daha değişmeyecek) komut durumları. */
export const AGENT_COMMAND_TERMINAL_STATUSES = ["SUCCEEDED", "FAILED", "EXPIRED", "CANCELLED"];
// ── Adaptörler ───────────────────────────────────────────────────────────────
export const AGENT_ADAPTER_KINDS = ["PUMP", "TANK_GAUGE", "FISCAL", "WASH", "TTS"];
export const agentAdapterKindSchema = z.enum(AGENT_ADAPTER_KINDS);
export const AGENT_ADAPTER_KIND_LABELS = {
    PUMP: "Pompa otomasyonu",
    TANK_GAUGE: "Tank probu",
    FISCAL: "ÖKC",
    WASH: "Araç yıkama",
    TTS: "Taşıt tanıma",
};
export const AGENT_ADAPTER_STATUSES = ["OK", "DEGRADED", "DOWN", "STOPPED"];
export const agentAdapterStatusSchema = z.enum(AGENT_ADAPTER_STATUSES);
export const AGENT_ADAPTER_STATUS_LABELS = {
    OK: "Çalışıyor",
    DEGRADED: "Sorunlu",
    DOWN: "Erişilemiyor",
    STOPPED: "Durdurulmuş",
};
// ── Cihaz durumları ──────────────────────────────────────────────────────────
export const AGENT_NOZZLE_STATUSES = ["IDLE", "CALLING", "AUTHORIZED", "FUELLING", "OFFLINE"];
export const agentNozzleStatusSchema = z.enum(AGENT_NOZZLE_STATUSES);
export const AGENT_NOZZLE_STATUS_LABELS = {
    IDLE: "Boşta",
    CALLING: "Çağırıyor",
    AUTHORIZED: "Yetkilendirildi",
    FUELLING: "Dolum yapıyor",
    OFFLINE: "Çevrimdışı",
};
export const AGENT_DEVICE_STATUSES = ["OK", "DEGRADED", "DOWN"];
export const agentDeviceStatusSchema = z.enum(AGENT_DEVICE_STATUSES);
export const AGENT_DEVICE_STATUS_LABELS = {
    OK: "Çalışıyor",
    DEGRADED: "Sorunlu",
    DOWN: "Erişilemiyor",
};
export const AGENT_ALARM_SEVERITIES = ["INFO", "WARNING", "CRITICAL"];
export const agentAlarmSeveritySchema = z.enum(AGENT_ALARM_SEVERITIES);
export const AGENT_ALARM_SEVERITY_LABELS = {
    INFO: "Bilgi",
    WARNING: "Uyarı",
    CRITICAL: "Kritik",
};
// ── Olay türleri ─────────────────────────────────────────────────────────────
/**
 * Protokol §3.1. Bu liste AÇIKTIR: bilinmeyen tür partiyi DÜŞÜRMEZ, yalnızca o kayıt
 * `UNSUPPORTED_EVENT_TYPE` ile reddedilir (eski sunucu yeni ajanı düşürmez).
 */
export const AGENT_EVENT_TYPES = [
    "NozzleTransaction",
    "TotalizerReading",
    "TankReading",
    "ZReport",
    "NozzleStatus",
    "DeviceStatus",
    "DeviceAlarm",
    "WashTransaction",
    "VehicleRead",
    // Prompt 18 — yıkama.
    "WashCycle",
    "WashUnitStatus",
    "WashActivation",
    "WashCreditIssued",
];
export const agentEventTypeSchema = z.enum(AGENT_EVENT_TYPES);
export const AGENT_EVENT_TYPE_LABELS = {
    NozzleTransaction: "Pompa satışı",
    TotalizerReading: "Tabanca endeksi",
    TankReading: "Tank okuması",
    ZReport: "Z raporu",
    NozzleStatus: "Tabanca durumu",
    DeviceStatus: "Cihaz durumu",
    DeviceAlarm: "Cihaz alarmı",
    WashTransaction: "Yıkama işlemi (eski)",
    VehicleRead: "Taşıt tanıma okuması",
    WashCycle: "Yıkama makinesi çalışması",
    WashUnitStatus: "Yıkama ünitesi durumu",
    WashActivation: "Yıkama başlatma / kodu",
    WashCreditIssued: "Self-servis kredisi",
};
// ── Senkron redleri ──────────────────────────────────────────────────────────
/** Akaryakıt alım çekirdeğinin kodları + ajan yoluna özgü kodlar. Hepsi KALICI redtir (yeniden gönderilmez). */
export const AGENT_SYNC_REJECTION_CODES = [
    ...FUEL_INGEST_REJECTION_CODES,
    "UNSUPPORTED_EVENT_TYPE",
    "UNKNOWN_FISCAL_DEVICE",
    "BUSINESS_DAY_LOCKED",
    "FEATURE_DISABLED",
    // Prompt 15: `PosSaleVoid` var olmayan bir satışı iptal etmeye çalışıyor — ortada kayıt yok, kalıcı rettir.
    "UNKNOWN_POS_SALE",
];
export const agentSyncRejectionCodeSchema = z.enum(AGENT_SYNC_REJECTION_CODES);
export const AGENT_SYNC_REJECTION_LABELS = {
    ...FUEL_INGEST_REJECTION_LABELS,
    UNSUPPORTED_EVENT_TYPE: "Desteklenmeyen olay türü",
    UNKNOWN_FISCAL_DEVICE: "ÖKC cihazı bulunamadı",
    BUSINESS_DAY_LOCKED: "İş günü kilitli",
    FEATURE_DISABLED: "Modül bu hesapta kapalı",
    UNKNOWN_POS_SALE: "İptal edilecek kasa satışı bulunamadı",
};
//# sourceMappingURL=enums.js.map